import { FormProvider, useForm } from "react-hook-form";
import { FiFilter, FiX } from "react-icons/fi";
import Langs from "../../utils/data/langs.json";
import countries from "../../utils/data/countries.json";
import { SearchTutorBar } from "./searchTutorBar";
import { TutorBrowseCard } from "./TutorBrowseCard";
import { Spinner } from "../animation/spinner";
import NotFoundWidget from "../widgets/NotFound";
import { SimplePagination } from "../global/simple-pagination";
import { useTutorSearch } from "../../utils/hooks/useTutorSearch";

const getLangLabel = (value) => Langs.find((lang) => lang.value === value)?.name || value;
const getCountryLabel = (iso) => countries.find((country) => country.iso === iso)?.name || iso;

const FilterChip = ({ label, onRemove }) => (
    <button
        type="button"
        onClick={onRemove}
        className="inline-flex items-center gap-1.5 rounded-full border border-nt-blue bg-nt-blue py-1.5 pl-3 pr-2 text-xs font-medium text-white transition hover:bg-nt-blue/90"
    >
        {label}
        <FiX size={14} />
    </button>
);

const TutorBrowsePage = ({ searchThunk, isPublic = false }) => {
    const methods = useForm({
        defaultValues: {
            teach: null,
            speak: null,
            country: null,
            price: "",
        },
    });
    const { getValues, reset, setValue, watch } = methods;

    const { tutors, page, totalPages, isLoading, handleSearch } = useTutorSearch(searchThunk);

    const filters = watch();
    const hasActiveFilters = Boolean(
        filters.teach || filters.speak || filters.country || filters.price
    );

    const runSearch = (nextPage = 0, overrides = {}) => {
        handleSearch(nextPage, { ...getValues(), ...overrides });
    };

    const clearAllFilters = () => {
        reset({
            teach: null,
            speak: null,
            country: null,
            price: "",
        });
        handleSearch(0, {});
    };

    const clearFilter = (field) => {
        const clearedValue = field === "price" ? "" : null;
        setValue(field, clearedValue);
        const nextValues = { ...getValues(), [field]: clearedValue };
        handleSearch(0, nextValues);
    };

    const activeChips = [
        filters.teach
            ? {
                  key: "teach",
                  label: `Learn: ${getLangLabel(filters.teach)}`,
                  onRemove: () => clearFilter("teach"),
              }
            : null,
        filters.speak
            ? {
                  key: "speak",
                  label: `Speak: ${getLangLabel(filters.speak)}`,
                  onRemove: () => clearFilter("speak"),
              }
            : null,
        filters.country
            ? {
                  key: "country",
                  label: `Country: ${getCountryLabel(filters.country)}`,
                  onRemove: () => clearFilter("country"),
              }
            : null,
        filters.price
            ? {
                  key: "price",
                  label: `Max ${filters.price} €`,
                  onRemove: () => clearFilter("price"),
              }
            : null,
    ].filter(Boolean);

    return (
        <div className="w-full">
            <div className="rounded-[20px] bg-white p-[30px]">
                <div className="mb-6 rounded-2xl border border-nt-grey-light bg-white p-4 sm:p-5">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-nt-grey-light pb-4">
                        <div className="flex items-center gap-2 text-nt-blue">
                            <FiFilter size={18} />
                            <span className="text-sm font-semibold">Search filters</span>
                        </div>
                        {hasActiveFilters ? (
                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="rounded-full border border-nt-grey-light px-3 py-1 text-xs font-medium text-nt-blue transition hover:border-nt-blue hover:bg-slate-50"
                            >
                                Clear all
                            </button>
                        ) : null}
                    </div>

                    <FormProvider {...methods}>
                        <SearchTutorBar handleSearch={(nextPage) => runSearch(nextPage)} />
                    </FormProvider>

                    {activeChips.length > 0 ? (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {activeChips.map((chip) => (
                                <FilterChip
                                    key={chip.key}
                                    label={chip.label}
                                    onRemove={chip.onRemove}
                                />
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-nt-grey">
                    <p>
                        {isLoading && tutors.length === 0 ? (
                            "Searching tutors…"
                        ) : tutors.length > 0 ? (
                            <>
                                Showing{" "}
                                <span className="font-semibold text-nt-blue">{tutors.length}</span>{" "}
                                tutor{tutors.length === 1 ? "" : "s"}
                                {totalPages > 0 ? (
                                    <>
                                        {" "}
                                        · Page{" "}
                                        <span className="font-semibold text-nt-blue">
                                            {page + 1}
                                        </span>{" "}
                                        of{" "}
                                        <span className="font-semibold text-nt-blue">
                                            {totalPages}
                                        </span>
                                    </>
                                ) : null}
                            </>
                        ) : hasActiveFilters ? (
                            "No tutors match your filters"
                        ) : (
                            "No tutors available yet"
                        )}
                    </p>
                    {isLoading ? (
                        <div className="flex items-center gap-2 text-nt-blue">
                            <Spinner className="h-5 w-5 border-2" />
                            <span className="text-xs">Loading…</span>
                        </div>
                    ) : null}
                </div>

                {tutors.length > 0 ? (
                    <div className={`flex flex-col gap-5 ${isLoading ? "opacity-60" : ""}`}>
                        {tutors.map((profile) => (
                            <TutorBrowseCard
                                key={profile._id}
                                profile={profile}
                                isPublic={isPublic}
                            />
                        ))}
                    </div>
                ) : !isLoading ? (
                    <NotFoundWidget
                        message={
                            hasActiveFilters
                                ? "No tutors match your current filters."
                                : "No tutors available yet."
                        }
                    />
                ) : (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                )}

                {tutors.length > 0 && totalPages > 0 ? (
                    <SimplePagination
                        page={page}
                        totalPages={totalPages}
                        onPageChange={(nextPage) => runSearch(nextPage)}
                        pageButtonCount={2}
                    />
                ) : null}
            </div>
        </div>
    );
};

export { TutorBrowsePage };
